import fetchApi, { FetchOptions } from '../util/fetchApi';
import { getObject } from '../util/mapObject';
import checkErrors from './checkErrors';

export const DEFAULT_ENDPOINT_DEFINITION = {
  module: "api",
  dataPath: (response: any) => response.data,
}

export type EndpointDefinitionDefaults = {
  module?: string
  method?: string
  xsrf?: boolean
  dataPath?: (response: any) => any
  options?: FetchOptions
}

export type EndpointDefinition<ParamType> = {
  url: string | ((data: ParamType) => string)
  checkErrors?: boolean,
  body?: (data: ParamType) => Record<string, any>
  formdata?: (data: ParamType) => Record<string, any>
  query?: (data: ParamType) => Record<string, any>
} & EndpointDefinitionDefaults

export type EndpointDefinitions<T> = {
  [K in keyof T]: EndpointDefinition<any>
}

export type EndpointFn<P, T> = P extends void
  ? (data: undefined, abortController?: AbortController) => Promise<T>
  : (data: P, abortController?: AbortController) => Promise<T>

export type ApiEndpoints<T> = {
  [K in keyof T]: EndpointFn<any, T[K]>
}

function ApiDefinition<T>({
  defaults,
  ...apiDef
}: {
  defaults?: Partial<EndpointDefinitionDefaults>
} & EndpointDefinitions<T>): ApiEndpoints<T> {
  const mergedDefaults = { ...DEFAULT_ENDPOINT_DEFINITION, ...defaults }

  const endpoints = Object.entries(apiDef).reduce(
    (_: Record<string, any>, [endpointName, endpointDefinition]) => {
      _[endpointName] = ApiEndpoint<any, T[keyof T]>(
        endpointDefinition as EndpointDefinition<any>,
        endpointName,
        mergedDefaults,
      )
      return _
    },
    {},
  ) as ApiEndpoints<T>

  return endpoints
}

export function ApiEndpoint<P = void, T = void>(
  endpointDefinition: EndpointDefinition<P>,
  endpointName?: string | null,
  defaults?: any,
): EndpointFn<P, T> {
  const definition: EndpointDefinition<P> = {
    ...DEFAULT_ENDPOINT_DEFINITION,
    ...defaults,
  }

  if (
    typeof endpointDefinition === "string" ||
    typeof endpointDefinition === "function"
  ) {
    definition.url = endpointDefinition
  } else {
    Object.assign(definition, endpointDefinition)
  }

  const m = /^(get|post|delete|put)/.exec(endpointName || "")
  if (!definition.method && m) {
    definition.method = m[0]
  }

  return async function apiEndpointFunction(
    data: P,
    abortController?: AbortController,
  ) {
    const _data = (data || {}) as P;
    const {
      checkErrors: optionsCheckErrors = true,
      method: _method,
      module='api',
      body,
      query,
      dataPath="data",
      formdata,
      options: defOptions,
    } = definition
    let { url, xsrf } = definition
    if (typeof url === "function") url = url(_data)
    const method = _method || (body ? "post" : "get")

    const options: FetchOptions = { ...(defOptions || {}) }

    if (xsrf) options.xsrf = true

    if (method !== "get" && formdata) options.formdata = formdata(_data)
    if (method !== "get" && body) options.data = body(_data)
    if (query) options.query = query(_data)
    if (abortController) {
      options.abortController = abortController
    }

    let response = await fetchApi[module][method](url, options)
    if (optionsCheckErrors) response = await checkErrors(response)
    if (dataPath && options.file !== true) {
        if (typeof dataPath === "function") {
            response = dataPath(response)
        } else {
            response = getObject(response, dataPath)
        }
    }

    return response
  } as unknown as EndpointFn<P, T>
}

export default ApiDefinition;
